/* Query and mutation hooks for the account itself — STORY-010.
 *
 * Its own feature rather than a corner of `organisations`, because it is about
 * the User: it knows about every Membership held and nothing inside a workspace
 * may. The API draws the same line, where none of these routes names an
 * Organisation or sets tenant context.
 */

import {
  accountExportSchema,
  accountStatusSchema,
  type CloseAccountRequest,
  closeAccountResponseSchema,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';

export const ACCOUNT_KEY = ['account'] as const;

/** Whether a closure is pending, and which workspaces still need an answer. */
export function useAccount() {
  return useQuery({
    queryKey: ACCOUNT_KEY,
    queryFn: () => request('/account', { schema: accountStatusSchema }),
    retry: false,
  });
}

/**
 * Everything held about the caller.
 *
 * A mutation rather than a query, because asking for it is an act: it is
 * rate-limited, it is written to the audit trail, and nothing should fetch
 * somebody's personal data because a component mounted.
 */
export function useExportAccount() {
  return useMutation({
    mutationFn: () => request('/account/export', { schema: accountExportSchema }),
  });
}

export function useCloseAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CloseAccountRequest) =>
      request('/account/closure', {
        method: 'POST',
        schema: closeAccountResponseSchema,
        body: values,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_KEY });
    },
  });
}

export function useCancelClosure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => request('/account/closure', { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_KEY });
    },
  });
}
